// src/components/inventory/AddStock.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbHelpers } from '../../db/database';
import apiClient from '../../api/client';
import { useToast } from '../../hooks/useToast';
import './AddStock.css';

function AddStock() {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    product: '',
    warehouse: '',
    quantity: 0,
    reorder_level: 10
  });
  
  const [errors, setErrors] = useState({});
  const [existingStock, setExistingStock] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.product && formData.warehouse) {
      checkExistingStock();
    } else {
      setExistingStock(null);
    }
  }, [formData.product, formData.warehouse]);

  async function loadData() {
    setLoading(true);
    try {
      // ⭐ Always fetch fresh data from API when online
      if (navigator.onLine) {
        const [prodsResponse, whseResponse, stksResponse] = await Promise.all([
          apiClient.get('/products/'),
          apiClient.get('/warehouses/'),
          apiClient.get('/stocks/')
        ]);
        
        const prods = prodsResponse.data.results || prodsResponse.data || [];
        const whse = whseResponse.data.results || whseResponse.data || [];
        const stks = stksResponse.data.results || stksResponse.data || [];
        
        setProducts(prods);
        setWarehouses(whse);
        setStocks(stks);
        
        // Update cache
        await Promise.all([
          dbHelpers.saveProducts(prods),
          dbHelpers.saveWarehouses(whse),
          dbHelpers.saveStocks(stks)
        ]);
      } else {
        // Offline: use cache
        const [prods, whse, stks] = await Promise.all([
          dbHelpers.getProducts(),
          dbHelpers.getWarehouses(),
          dbHelpers.getStocks()
        ]);
        setProducts(prods);
        setWarehouses(whse);
        setStocks(stks);
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load data');
    }
    setLoading(false);
  }

  function checkExistingStock() {
    const existing = stocks.find(s =>
      String(s.product) === String(formData.product) &&
      String(s.warehouse) === String(formData.warehouse)
    );
    setExistingStock(existing || null);
  }

  function getStockLevel(productId) {
    const productStocks = stocks.filter(s => String(s.product) === String(productId));
    return productStocks.reduce((sum, s) => sum + (s.quantity || 0), 0);
  }

  function getProductWarehouses(productId) {
    return stocks
      .filter(s => String(s.product) === String(productId))
      .map(s => s.warehouse_name)
      .filter(Boolean);
  }

  function validateForm() {
    const newErrors = {};
    
    if (!formData.product) {
      newErrors.product = 'Please select a product';
    }
    
    if (!formData.warehouse) {
      newErrors.warehouse = 'Please select a warehouse';
    }
    
    if (existingStock) {
      const productName = products.find(p => String(p.id) === String(formData.product))?.name;
      const warehouseName = warehouses.find(w => String(w.id) === String(formData.warehouse))?.name;
      newErrors.general = `Stock entry already exists for "${productName}" at "${warehouseName}". Use "Adjust Stock" to modify it or select a different warehouse.`;
    }
    
    if (formData.quantity < 0) {
      newErrors.quantity = 'Quantity cannot be negative';
    }
    
    if (formData.reorder_level < 0) {
      newErrors.reorder_level = 'Reorder level cannot be negative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const product = products.find(p => String(p.id) === String(formData.product));
      const warehouse = warehouses.find(w => String(w.id) === String(formData.warehouse));

      const stockData = {
        product: formData.product,
        warehouse: formData.warehouse,
        quantity: parseInt(formData.quantity),
        reorder_level: parseInt(formData.reorder_level)
      };

      const response = await apiClient.post('/stocks/', stockData);
      const createdStock = response.data;

      await dbHelpers.saveStocks([{
        ...createdStock,
        product_name: product?.name,
        warehouse_name: warehouse?.name
      }]);

      toast.success('Stock entry created successfully');
      navigate('/inventory/stocks');
    } catch (error) {
      console.log('Full error:', error.response?.data);
      
      let errorMsg = 'Failed to create stock';
      
      if (error.response?.data?.non_field_errors?.[0]) {
        errorMsg = error.response.data.non_field_errors[0];
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      console.log('Showing error:', errorMsg);
      toast.error(errorMsg);
      setErrors({ general: errorMsg });
    }
    setSaving(false);
  }

  function handleChange(field, value) {
    setFormData({
      ...formData,
      [field]: value
    });
    
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
    
    if (errors.general) {
      setErrors({
        ...errors,
        general: null
      });
    }
  }

  // ⭐ Get available warehouses for selected product
  function getAvailableWarehouses() {
    if (!formData.product) return warehouses;
    
    const productStockWarehouses = stocks
      .filter(s => String(s.product) === String(formData.product))
      .map(s => String(s.warehouse));
    
    return warehouses.filter(w => !productStockWarehouses.includes(String(w.id)));
  }

  // ⭐ Check if product can be added to any warehouse
  function canAddProduct(productId) {
    const productStockWarehouses = stocks
      .filter(s => String(s.product) === String(productId))
      .map(s => String(s.warehouse));
    
    return productStockWarehouses.length < warehouses.length;
  }

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const availableWarehouses = getAvailableWarehouses();

  return (
    <div className="add-stock-container fade-in">
      <div className="breadcrumb">
        <button onClick={() => navigate('/inventory/stocks')} className="btn-link">
          ← Back to Stocks
        </button>
      </div>

      <div className="form-card">
        <div className="form-header">
          <h1>📦 Add New Stock</h1>
          <p className="subtitle">Create a new stock entry for a product in a warehouse</p>
        </div>

        {errors.general && (
          <div className="alert-error">
            ❌ {errors.general}
          </div>
        )}

        {existingStock && (
          <div className="alert-warning">
            ⚠️ A stock entry already exists for this combination.
            <br />
            <strong>Current stock: {existingStock.quantity} units at {existingStock.warehouse_name}</strong>
            <br />
            <button
              onClick={() => navigate(`/inventory/stock-adjustment/${formData.product}`)}
              className="btn-link-warning"
            >
              → Go to Adjust Stock instead
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>📋 Basic Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Product *</label>
                <select
                  value={formData.product}
                  onChange={(e) => handleChange('product', e.target.value)}
                  className={`form-input ${errors.product ? 'error' : ''}`}
                >
                  <option value="">Select Product</option>
                  {products.map(p => {
                    const stockLevel = getStockLevel(p.id);
                    const productWarehouses = getProductWarehouses(p.id);
                    const canAdd = canAddProduct(p.id);
                    
                    return (
                      <option 
                        key={p.id} 
                        value={p.id}
                        disabled={!canAdd}
                      >
                        {p.name} ({p.sku}) - Stock: {stockLevel}
                        {!canAdd ? ' - Already in all warehouses' : ''}
                        {productWarehouses.length > 0 ? ` (In: ${productWarehouses.join(', ')})` : ''}
                      </option>
                    );
                  })}
                </select>
                {errors.product && <span className="error-text">{errors.product}</span>}
              </div>

              <div className="form-group">
                <label>Warehouse *</label>
                <select
                  value={formData.warehouse}
                  onChange={(e) => handleChange('warehouse', e.target.value)}
                  className={`form-input ${errors.warehouse ? 'error' : ''}`}
                  disabled={!formData.product}
                >
                  <option value="">
                    {!formData.product ? 'Select a product first' : 'Select Warehouse'}
                  </option>
                  {availableWarehouses.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
                {errors.warehouse && <span className="error-text">{errors.warehouse}</span>}
                {formData.product && availableWarehouses.length === 0 && (
                  <span className="help-text" style={{color: '#f59e0b'}}>
                    This product is already in all warehouses. Use "Adjust Stock" instead.
                  </span>
                )}
                {formData.product && availableWarehouses.length > 0 && (
                  <span className="help-text">
                    {availableWarehouses.length} warehouse(s) available for this product
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>📊 Stock Levels</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Initial Quantity *</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                  className={`form-input ${errors.quantity ? 'error' : ''}`}
                  min="0"
                  placeholder="Enter initial quantity"
                />
                {errors.quantity && <span className="error-text">{errors.quantity}</span>}
                <span className="help-text">Starting quantity in this warehouse</span>
              </div>

              <div className="form-group">
                <label>Reorder Level *</label>
                <input
                  type="number"
                  value={formData.reorder_level}
                  onChange={(e) => handleChange('reorder_level', e.target.value)}
                  className={`form-input ${errors.reorder_level ? 'error' : ''}`}
                  min="0"
                  placeholder="Enter reorder level"
                />
                {errors.reorder_level && <span className="error-text">{errors.reorder_level}</span>}
                <span className="help-text">Alert when stock falls below this level</span>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/inventory/stocks')}
              className="btn btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || existingStock || (formData.product && availableWarehouses.length === 0)}
            >
              {saving ? 'Creating...' : '✅ Create Stock Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddStock;
